import React, { useEffect, useState, useMemo } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import { createGlobalStyle } from "styled-components";
import { prepareWidget } from "../common/widget-util";
import MainWidget from "./main-widget";
import DetailCardDialog from "./detail-card-dialog";
import { useMDialog } from "../common/m-dialog";

const GlobalStyle = createGlobalStyle`
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-1px); }
    20%, 40%, 60%, 80% { transform: translateX(1px); }
  }
`;

const ResponsiveGridLayout = WidthProvider(Responsive);

const DashboardCard = React.memo(
  ({
    card,
    drag = false,
    layoutSize = "sm",
    onLayoutChange,
    onStateUpdate,
    onValueUpdate,
    onTriggerEvent,
    onTitleHold,
  }) => {
    // Get the bind widget for the title if it exists
    const titleBindWidget = card.titleBindWidgetId
      ? card.widgets.find((w) => w.id === card.titleBindWidgetId)
      : null;
    const [width, setWidth] = useState(1);
    const [layouts, setLayouts] = useState(null);
    const [showContent, setShowContent] = useState(false);
    const [isShaking, setIsShaking] = useState(false);

    const dialogHash = `detail-card-${card.id}`;
    const openDetailDialog = useMDialog(dialogHash);

    // Prepare widgets once when card changes
    const preparedWidgets = useMemo(() => {
      const widgets = card.widgets
        .filter((widget) => widget.layouts[layoutSize])
        .map((widget) => {
          prepareWidget(widget);
          return widget;
        });
      return widgets;
    }, [card.widgets]);

    const handleLayoutChange = (layout) => {
      if (drag && onLayoutChange) {
        const updatedLayouts = layout.map((item) => ({
          id: item.i,
          layoutSize,
          layout: {
            x: item.x,
            y: item.y,
            w: item.w,
            h: item.h,
          },
        }));

        onLayoutChange(updatedLayouts);
      }
    };

    useEffect(() => {
      const cardWidth = card.layouts[layoutSize].w * 4;
      setWidth(cardWidth);
      const lo = card.widgets
        .filter((w) => w.layouts[layoutSize])
        .map((w) => {
          return {
            i: w.id,
            x: w.layouts[layoutSize].x,
            y: w.layouts[layoutSize].y,
            w: w.layouts[layoutSize].w,
            h: w.layouts[layoutSize].h,
          };
        });
      setLayouts(lo);

      const timer = setTimeout(() => {
        setShowContent(true);
      }, 100);

      return () => clearTimeout(timer);
    }, [card, layoutSize]);

    const renderedWidgets = useMemo(() => {
      return preparedWidgets.map((widget) => (
        <div
          key={widget.id}
          style={{
            height: "100%",
            width: "100%",
            display: "flex",
            transition: "none !important",
            animation: "none !important",
            cursor: drag ? "move" : "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
              padding: "2px",
              transition: "none !important",
              animation: "none !important",
              pointerEvents: drag ? "none" : "auto",
              userSelect: drag ? "none" : "auto",
              cursor: drag ? "move" : "auto",
            }}
          >
            <MainWidget
              widget={widget}
              onValueUpdate={onValueUpdate}
              onStateUpdate={onStateUpdate}
              onTriggerEvent={onTriggerEvent}
              onTitleClick={() => {
                openDetailDialog();
              }}
              onTitleHold={(widget) => {
                setIsShaking(true);
                setTimeout(() => setIsShaking(false), 500);
                if (onTitleHold) {
                  onTitleHold(widget);
                }
              }}
              titleBindWidget={titleBindWidget}
            />
          </div>
        </div>
      ));
    }, [
      preparedWidgets,
      onValueUpdate,
      onTriggerEvent,
      onStateUpdate,
      drag,
      layoutSize,
    ]);

    return (
      <>
        <GlobalStyle />
        {layouts && (
          <ResponsiveGridLayout
            className="layout"
            layouts={{
              xs: layouts,
            }}
            breakpoints={{ xs: 0 }}
            cols={{ xs: width }}
            rowHeight={8}
            compactType={null}
            preventCollision={true}
            margin={[0, 0]}
            isDraggable={drag}
            isResizable={drag}
            onLayoutChange={handleLayoutChange}
            useCSSTransforms={false}
            transitionDuration={0}
            animate={false}
            autoSize={false}
            style={{
              userSelect: "none",
              WebkitUserSelect: "none",
              MozUserSelect: "none",
              msUserSelect: "none",
              opacity: showContent ? 1 : 0,
              transition: "opacity 0.1s ease-in-out",
              animation: isShaking ? "shake 0.5s ease-in-out" : "none",
            }}
          >
            {renderedWidgets}
          </ResponsiveGridLayout>
        )}

        <DetailCardDialog
          card={card}
          onValueUpdate={onValueUpdate}
          onStateUpdate={onStateUpdate}
          onTriggerEvent={onTriggerEvent}
        />
      </>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.card === nextProps.card &&
      prevProps.isEditTitle === nextProps.isEditTitle &&
      prevProps.drag === nextProps.drag &&
      prevProps.layoutSize === nextProps.layoutSize
    );
  }
);

export default DashboardCard;

DashboardCard.displayName = "DashboardCard";
